import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

type JointPermissionType = 'read' | 'edit';

export type PalettePermissionDb = {
  paletteId: string;
  jointPermission: JointPermissionType;
}
const initial: PalettePermissionDb = {
  paletteId: '',
  jointPermission: 'read'
};

export type PalettePermissionDbHashType = {
  [key in string]: PalettePermissionDb;
}

export type PalettePermissionAddPayload = {
  id?: string;
} & PalettePermissionDb;
export type PalettePermissionUpdatePayload = {
  id: string;
} & { [P in keyof PalettePermissionDb]?: PalettePermissionDb[P] };

export type PalettePermissionDbState = {
  palettePermissions: PalettePermissionDbHashType;
}

export const generatePalettePermissionId = (): string => generateId('pp');
let initialState: PalettePermissionDbState = { palettePermissions: {} };
const key = 'palettePermissions';

const palettePermissionDbSlice = createSlice({
  name: 'palettePermissionDb',
  initialState,
  reducers: {
    initializePalettePermissions(state: PalettePermissionDbState) {
      state.palettePermissions = {};
    },
    addPalettePermission(state: PalettePermissionDbState, action: PayloadAction<PalettePermissionAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generatePalettePermissionId();
      state.palettePermissions[id] = obj;
      saveData(key, id, obj);
    },
    updatePalettePermission(state: PalettePermissionDbState, action: PayloadAction<PalettePermissionUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.palettePermissions[id] === undefined) {
        console.log(`*** PalettePermission not found: ${id}`);
      } else {
        const newData = { ...state.palettePermissions[id], ...obj };
        state.palettePermissions[id] = newData;
        saveData(key, id, newData);
      }
    },
    deletePalettePermission(state: PalettePermissionDbState, action: PayloadAction<PalettePermissionUpdatePayload>) {
      const id = action.payload.id;
      delete state.palettePermissions[id];
      deleteData(key, id);
    },
    savePalettePermissions(state: PalettePermissionDbState) {
      saveAllData(key, state.palettePermissions);
    },
    signUpPalettePermissions(state: PalettePermissionDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.palettePermissions, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: PalettePermissionDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.palettePermissions = updatedValues;
      if (changed) saveAllData(key, state.palettePermissions);
    })
  }
})

export const { initializePalettePermissions, addPalettePermission, updatePalettePermission, deletePalettePermission, savePalettePermissions, signUpPalettePermissions } = palettePermissionDbSlice.actions;
export default palettePermissionDbSlice.reducer;
